<template>
    <div class="first-change-password-form w-100 max-w-448-px">
        <div class="text-left mb-3s">
            <b-button
                type="button"
                variant="transparent"
                class="text-grey text-semi-xl font-weight-600 p-0"
                @click="$emit('back')"
            >
                <i class="far fa-chevron-left"></i>
                {{ $t('button.back') }}
            </b-button>
        </div>
        <b-form @submit.stop.prevent="submit">
            <b-form-group
                :label="$t('auth.password_new')"
                label-class="font-weight-600 text-grey text-semi-xl pb-2s"
            >
                <b-input-group>
                    <b-form-input
                        v-model="form.password"
                        :formatter="removeSpace"
                        :maxlength="maxLength.password"
                        :placeholder="$t('auth.password_placeholder')"
                        :type="showPassword ? 'text' : 'password'"
                        :autocomplete="'new-password'"
                        :tabindex="1"
                        size="lg"
                        class="bg-catskill-white border-0 h-48-px"
                    ></b-form-input>
                    <template #append>
                        <b-button
                            class="
                                text-grey
                                bg-catskill-white bg-hover-catskill-white
                                border-0
                            "
                            variant="outline-catskill-white"
                            @click="showPassword = !showPassword"
                        >
                            <i
                                class="far text-grey font-weight-600 text-xl"
                                :class="
                                    !showPassword ? 'fa-eye-slash' : 'fa-eye'
                                "
                            ></i>
                        </b-button>
                    </template>
                </b-input-group>
                <b-form-invalid-feedback
                    :state="!!error.password ? false : null"
                >
                    {{ error.password }}
                </b-form-invalid-feedback>
                <b-form-text v-if="!error.password">
                    {{ $t('error.INVALID_PASSWORD_REGISTER') }}
                </b-form-text>
            </b-form-group>
            <b-form-group
                :label="$t('auth.password_confirmation')"
                label-class="font-weight-600 text-grey text-semi-xl pb-2s"
            >
                <b-input-group>
                    <b-form-input
                        v-model="form.passwordConfirmation"
                        :formatter="removeSpace"
                        :maxlength="maxLength.password"
                        :placeholder="$t('auth.password_placeholder')"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :autocomplete="'new-password'"
                        :tabindex="2"
                        size="lg"
                        class="bg-catskill-white border-0 h-48-px"
                    ></b-form-input>
                    <template #append>
                        <b-button
                            variant="outline-catskill-white"
                            class="
                                text-grey
                                bg-catskill-white bg-hover-catskill-white
                                border-0
                            "
                            @click="showConfirmPassword = !showConfirmPassword"
                        >
                            <i
                                :class="
                                    !showConfirmPassword
                                        ? 'fa-eye-slash'
                                        : 'fa-eye'
                                "
                                class="far text-grey font-weight-600 text-xl"
                            ></i>
                        </b-button>
                    </template>
                </b-input-group>
                <b-form-invalid-feedback
                    :state="!!error.passwordConfirmation ? false : null"
                >
                    {{ error.passwordConfirmation }}
                </b-form-invalid-feedback>
            </b-form-group>
            <b-button
                type="submit"
                :disabled="submitting"
                block
                variant="cinnabar"
                size="lg"
                :tabindex="3"
                class="text-white text-semi-xl font-weight-600 h-48-px mb-3s"
            >
                <i v-if="submitting" class="far fa-spinner fa-spin mr-2"></i>
                {{ submitting ? $t('common.processing') : $t('common.save') }}
            </b-button>
        </b-form>
    </div>
</template>

<script>
import { REGEX } from '@/constants/regex';
import errorMixin from '@/mixins/error';
import { setFirstChangePassword } from '@/services/user.service';

export default {
    name: 'FirstChangePasswordForm',
    props: {
        dataLogin: {
            type: Object,
            required: true,
            default() {
                return {};
            },
        },
    },
    mixins: [errorMixin],
    data() {
        return {
            form: {
                password: '',
                passwordConfirmation: '',
            },
            error: {
                password: '',
                passwordConfirmation: '',
            },
            maxLength: {
                password: 32,
            },
            minLength: {
                password: 8,
            },
            showPassword: false,
            showConfirmPassword: false,
            submitting: false,
        };
    },
    methods: {
        setEmptyError() {
            this.$helpers.resetAllPropertiesObjects(this.error);
        },
        validateForm(form) {
            this.setEmptyError();
            const rules = {
                password: [
                    'min:' + this.minLength.password,
                    'max:' + this.maxLength.password,
                    'regexPassword',
                    'isUnicode',
                ],
                passwordConfirmation: ['same:password'],
            };
            const messages = {
                'password.min': this.$t('error.MAX_PASSWORD_REGISTER', {
                    from: this.minLength.password,
                    to: this.maxLength.password,
                }),
                'password.max': this.$t('error.MAX_PASSWORD_REGISTER', {
                    from: this.minLength.password,
                    to: this.maxLength.password,
                }),
                'passwordConfirmation.same': this.$t(
                    'error.SAME_PASSWORD_REGISTER'
                ),
            };
            const extend = {
                isUnicode: [
                    this.$t('error.INVALID_PASSWORD'),
                    ({ value }) => {
                        return REGEX.NOT_UNICODE.test(value);
                    },
                ],
                regexPassword: [
                    this.$t('error.INVALID_PASSWORD_REGISTER'),
                    ({ value }) => {
                        return REGEX.PASSWORD.test(value);
                    },
                ],
            };

            const validateErrors = this.$helpers.validateForm(
                form,
                rules,
                messages,
                extend
            );
            if (
                this.$helpers.isObject(validateErrors) &&
                !this.$helpers.isEmptyObj(validateErrors)
            ) {
                Object.keys(this.error).forEach((i) => {
                    if (validateErrors[i]) {
                        this.error[i] = validateErrors[i];
                    }
                });
                return false;
            }
            return true;
        },
        async submit() {
            const formData = this.$helpers.trimForm(this.form);
            if (!this.validateForm(formData)) {
                return;
            }
            try {
                this.submitting = true;
                const { data } = await setFirstChangePassword({
                    ...formData,
                    token: this.dataLogin.accessToken,
                });
                this.$emit('onPasswordUpdated', data);
            } catch (e) {
                this.handleThrowError(e);
            } finally {
                this.submitting = false;
            }
        },
        removeSpace(string) {
            return string.replace(/\s/g, '');
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'FirstChangePasswordForm';
</style>
